/** @format */

import React, { useState, useEffect } from "react";
import { Tab } from "@headlessui/react";
import { useOfflinePayments } from "../hooks/useOfflinePayments";
import { useOnlinePayments } from "../hooks/useOnlinePayments";
import { useSimpleUsers } from "../hooks/useSimpleUsers";
import { useAuth } from "../contexts/AuthContext";
import {
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ClockIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingSpinner from "../components/LoadingSpinner";

// Utility function to safely format timestamps
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "N/A";

  try {
    // Check if it's a Firestore timestamp
    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      return format(timestamp.toDate(), "MMM dd, yyyy");
    }

    // Check if it's a regular Date object
    if (timestamp instanceof Date) {
      return format(timestamp, "MMM dd, yyyy");
    }

    // Check if it's a timestamp number or string
    if (typeof timestamp === "number" || typeof timestamp === "string") {
      return format(new Date(timestamp), "MMM dd, yyyy");
    }

    return "N/A";
  } catch (error) {
    console.warn("Error formatting timestamp:", error);
    return "N/A";
  }
};

const PaymentsPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingPaymentId, setLoadingPaymentId] = useState(null);

  // Hooks
  const {
    offlinePayments,
    loading: offlineLoading,
    approveOfflinePayment,
    rejectOfflinePayment,
    deleteOfflinePayment,
  } = useOfflinePayments();
  const {
    onlinePayments,
    loading: onlineLoading,
    approveOnlinePayment,
    rejectOnlinePayment,
    deleteOnlinePayment,
  } = useOnlinePayments();
  const { users } = useSimpleUsers();
  const { currentUser } = useAuth();

  const loading = offlineLoading || onlineLoading;

  // Filter and sort payments based on status
  const filteredOfflinePayments = offlinePayments
    .filter((payment) => {
      if (statusFilter === "all") return true;
      return payment.status === statusFilter;
    })
    .sort((a, b) => {
      // Sort by createdAt descending, with fallback for missing timestamps
      const dateA = a.createdAt
        ? a.createdAt.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt)
        : new Date(0);
      const dateB = b.createdAt
        ? b.createdAt.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt)
        : new Date(0);
      return dateB - dateA;
    });

  const filteredOnlinePayments = onlinePayments
    .filter((payment) => {
      if (statusFilter === "all") return true;
      return payment.status === statusFilter;
    })
    .sort((a, b) => {
      // Sort by createdAt descending, with fallback for missing timestamps
      const dateA = a.createdAt
        ? a.createdAt.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt)
        : new Date(0);
      const dateB = b.createdAt
        ? b.createdAt.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt)
        : new Date(0);
      return dateB - dateA;
    });

  // Debug logging to understand data structure
  useEffect(() => {
    if (offlinePayments.length > 0) {
      console.log("=== OFFLINE PAYMENT DATA ANALYSIS ===");
      console.log("Total offline payments:", offlinePayments.length);

      // Log first 3 payments in detail
      offlinePayments.slice(0, 3).forEach((payment, index) => {
        console.log(`\n--- Offline Payment ${index + 1} ---`);
        console.log("Payment ID:", payment.id);
        console.log("Full payment object:", payment);

        // Check for amount fields
        console.log("Amount fields found:");
        if (payment.paymentDetails)
          console.log("- paymentDetails:", payment.paymentDetails);
        if (payment.amount)
          console.log(
            "- amount:",
            payment.amount,
            "Type:",
            typeof payment.amount
          );
        if (payment.total)
          console.log("- total:", payment.total, "Type:", typeof payment.total);
        if (payment.paymentAmount)
          console.log(
            "- paymentAmount:",
            payment.paymentAmount,
            "Type:",
            typeof payment.paymentAmount
          );

        // Check for product fields
        console.log("Product fields found:");
        if (payment.productDetails)
          console.log("- productDetails:", payment.productDetails);
        if (payment.productName)
          console.log("- productName:", payment.productName);
        if (payment.product) console.log("- product:", payment.product);
        if (payment.courseName)
          console.log("- courseName:", payment.courseName);
        if (payment.itemName) console.log("- itemName:", payment.itemName);
        if (payment.description)
          console.log("- description:", payment.description);
        if (payment.notes) console.log("- notes:", payment.notes);

        // Check for user fields
        console.log("User fields found:");
        if (payment.userId) console.log("- userId:", payment.userId);
        if (payment.referredBy)
          console.log("- referredBy:", payment.referredBy);
        if (payment.status) console.log("- status:", payment.status);

        // Check for timestamp fields
        console.log("Timestamp fields found:");
        if (payment.createdAt)
          console.log(
            "- createdAt:",
            payment.createdAt,
            "Type:",
            typeof payment.createdAt
          );
        if (payment.updatedAt)
          console.log(
            "- updatedAt:",
            payment.updatedAt,
            "Type:",
            typeof payment.updatedAt
          );

        // List all available fields
        console.log("All available fields:", Object.keys(payment));
      });

      // Check for common patterns
      const allFields = new Set();
      offlinePayments.forEach((payment) => {
        Object.keys(payment).forEach((key) => allFields.add(key));
      });
      console.log("\n=== ALL UNIQUE FIELDS IN OFFLINE PAYMENTS ===");
      console.log(Array.from(allFields).sort());
    } else {
      console.log("No offline payments found");
    }

    if (onlinePayments.length > 0) {
      console.log("\n=== ONLINE PAYMENT DATA ANALYSIS ===");
      console.log("Total online payments:", onlinePayments.length);

      // Log first 3 payments in detail
      onlinePayments.slice(0, 3).forEach((payment, index) => {
        console.log(`\n--- Online Payment ${index + 1} ---`);
        console.log("Payment ID:", payment.id);
        console.log("Full payment object:", payment);

        // Check for amount fields
        console.log("Amount fields found:");
        if (payment.paymentDetails)
          console.log("- paymentDetails:", payment.paymentDetails);
        if (payment.amount)
          console.log(
            "- amount:",
            payment.amount,
            "Type:",
            typeof payment.amount
          );
        if (payment.total)
          console.log("- total:", payment.total, "Type:", typeof payment.total);
        if (payment.paymentAmount)
          console.log(
            "- paymentAmount:",
            payment.paymentAmount,
            "Type:",
            typeof payment.paymentAmount
          );

        // Check for product fields
        console.log("Product fields found:");
        if (payment.productDetails)
          console.log("- productDetails:", payment.productDetails);
        if (payment.productName)
          console.log("- productName:", payment.productName);
        if (payment.product) console.log("- product:", payment.product);
        if (payment.courseName)
          console.log("- courseName:", payment.courseName);
        if (payment.itemName) console.log("- itemName:", payment.itemName);
        if (payment.description)
          console.log("- description:", payment.description);
        if (payment.notes) console.log("- notes:", payment.notes);

        // Check for user fields
        console.log("User fields found:");
        if (payment.userId) console.log("- userId:", payment.userId);
        if (payment.referredBy)
          console.log("- referredBy:", payment.referredBy);
        if (payment.status) console.log("- status:", payment.status);

        // Check for timestamp fields
        console.log("Timestamp fields found:");
        if (payment.createdAt)
          console.log(
            "- createdAt:",
            payment.createdAt,
            "Type:",
            typeof payment.createdAt
          );
        if (payment.updatedAt)
          console.log(
            "- updatedAt:",
            payment.updatedAt,
            "Type:",
            typeof payment.updatedAt
          );

        // List all available fields
        console.log("All available fields:", Object.keys(payment));
      });

      // Check for common patterns
      const allFields = new Set();
      onlinePayments.forEach((payment) => {
        Object.keys(payment).forEach((key) => allFields.add(key));
      });
      console.log("\n=== ALL UNIQUE FIELDS IN ONLINE PAYMENTS ===");
      console.log(Array.from(allFields).sort());
    } else {
      console.log("No online payments found");
    }
  }, [offlinePayments, onlinePayments]);

  // Get user details for a payment
  const getUserDetails = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user || { name: "Unknown User", email: "Unknown" };
  };

  // Handle approval
  const handleApprove = (payment, type) => {
    setSelectedPayment({ ...payment, type });
    setConfirmAction("approve");
    setShowConfirmDialog(true);
  };

  // Handle rejection
  const handleReject = (payment, type) => {
    setSelectedPayment({ ...payment, type });
    setShowRejectionModal(true);
  };

  // Handle deletion
  const handleDelete = (payment, type) => {
    setSelectedPayment({ ...payment, type });
    setConfirmAction("delete");
    setShowConfirmDialog(true);
  };

  // Execute confirmed action
  const executeAction = async () => {
    if (!selectedPayment) return;

    try {
      setActionLoading(true);
      setLoadingPaymentId(selectedPayment.id);

      if (confirmAction === "approve") {
        if (selectedPayment.type === "offline") {
          await approveOfflinePayment(selectedPayment.id, currentUser.uid);
        } else {
          await approveOnlinePayment(selectedPayment.id, currentUser.uid);
        }
      } else if (confirmAction === "delete") {
        if (selectedPayment.type === "offline") {
          await deleteOfflinePayment(selectedPayment.id);
        } else {
          await deleteOnlinePayment(selectedPayment.id);
        }
      }

      setShowConfirmDialog(false);
      setSelectedPayment(null);
      setConfirmAction(null);
    } catch (error) {
      console.error("Error executing action:", error);
    } finally {
      setActionLoading(false);
      setLoadingPaymentId(null);
    }
  };

  // Execute rejection
  const executeRejection = async () => {
    if (!selectedPayment || !rejectionReason.trim()) return;

    try {
      setActionLoading(true);
      setLoadingPaymentId(selectedPayment.id);

      if (selectedPayment.type === "offline") {
        await rejectOfflinePayment(
          selectedPayment.id,
          currentUser.uid,
          rejectionReason
        );
      } else {
        await rejectOnlinePayment(
          selectedPayment.id,
          currentUser.uid,
          rejectionReason
        );
      }

      setShowRejectionModal(false);
      setSelectedPayment(null);
      setRejectionReason("");
    } catch (error) {
      console.error("Error executing rejection:", error);
    } finally {
      setActionLoading(false);
      setLoadingPaymentId(null);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: ClockIcon },
      approved: { color: "bg-green-100 text-green-800", icon: CheckCircleIcon },
      rejected: { color: "bg-red-100 text-red-800", icon: XCircleIcon },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Payment table component
  const PaymentTable = ({ payments, type }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Product Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Referred By
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {payments
            .filter((payment) => payment && payment.id) // Filter out invalid payments
            .map((payment) => {
              const user = getUserDetails(payment.userId);
              const referredByUser = payment.referredBy
                ? getUserDetails(payment.referredBy)
                : null;

              return (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      <div className="text-lg font-bold text-green-600">
                        {payment.currency || "GHS"}{" "}
                        {payment.total?.toFixed(2) || "0.00"}
                      </div>
                      {payment.items && payment.items.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {payment.items.length} item
                          {payment.items.length > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {payment.items && payment.items.length > 0 ? (
                        <div className="space-y-2">
                          {payment.items.map((item, index) => (
                            <div
                              key={item.id || index}
                              className="border-l-2 border-blue-200 pl-3"
                            >
                              <div className="font-medium text-blue-900">
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-600 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {item.type}
                                </span>
                                <span className="text-green-600 font-medium">
                                  {payment.currency || "GHS"}{" "}
                                  {item.amount?.toFixed(2) || "0.00"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">
                          No items found
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {referredByUser ? (
                      <div className="text-sm text-gray-900">
                        {referredByUser.name}
                      </div>
                    ) : (
                      <span className="text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(payment.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(payment.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {payment.status === "pending" && (
                        <>
                          <button
                            onClick={() => handleApprove(payment, type)}
                            disabled={actionLoading}
                            className={`text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                              loadingPaymentId === payment.id &&
                              confirmAction === "approve"
                                ? "animate-pulse"
                                : ""
                            }`}
                          >
                            {loadingPaymentId === payment.id &&
                            confirmAction === "approve" ? (
                              <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <CheckCircleIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleReject(payment, type)}
                            disabled={actionLoading}
                            className={`text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                              loadingPaymentId === payment.id &&
                              showRejectionModal
                                ? "animate-pulse"
                                : ""
                            }`}
                          >
                            {loadingPaymentId === payment.id &&
                            showRejectionModal ? (
                              <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <XCircleIcon className="h-5 w-5" />
                            )}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(payment, type)}
                        disabled={actionLoading}
                        className={`text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                          loadingPaymentId === payment.id &&
                          confirmAction === "delete"
                            ? "animate-pulse"
                            : ""
                        }`}
                      >
                        {loadingPaymentId === payment.id &&
                        confirmAction === "delete" ? (
                          <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <TrashIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Payments Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage online and offline payment requests
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex border-b border-gray-200">
            <Tab
              className={({ selected }) =>
                `flex-1 px-6 py-4 text-sm font-medium text-center border-b-2 transition-colors ${
                  selected
                    ? "border-primary-500 text-primary-600 bg-primary-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`
              }
            >
              <div className="flex items-center justify-center space-x-2">
                <CreditCardIcon className="h-5 w-5" />
                <span>Online Payments ({filteredOnlinePayments.length})</span>
              </div>
            </Tab>
            <Tab
              className={({ selected }) =>
                `flex-1 px-6 py-4 text-sm font-medium text-center border-b-2 transition-colors ${
                  selected
                    ? "border-primary-500 text-primary-600 bg-primary-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`
              }
            >
              <div className="flex items-center justify-center space-x-2">
                <BanknotesIcon className="h-5 w-5" />
                <span>Offline Payments ({filteredOfflinePayments.length})</span>
              </div>
            </Tab>
          </Tab.List>
          <Tab.Panels className="p-6">
            <Tab.Panel>
              {/* Online Payments */}
              {filteredOnlinePayments.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No online payments
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No online payment requests found.
                  </p>
                </div>
              ) : (
                <PaymentTable payments={filteredOnlinePayments} type="online" />
              )}
            </Tab.Panel>
            <Tab.Panel>
              {/* Offline Payments */}
              {filteredOfflinePayments.length === 0 ? (
                <div className="text-center py-12">
                  <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No offline payments
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No offline payment requests found.
                  </p>
                </div>
              ) : (
                <PaymentTable
                  payments={filteredOfflinePayments}
                  type="offline"
                />
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={executeAction}
        title={
          confirmAction === "approve" ? "Approve Payment" : "Delete Payment"
        }
        message={
          confirmAction === "approve"
            ? "Are you sure you want to approve this payment? This will update the referred user's earnings."
            : "Are you sure you want to delete this payment? This action cannot be undone."
        }
        confirmText={confirmAction === "approve" ? "Approve" : "Delete"}
        confirmColor={confirmAction === "approve" ? "green" : "red"}
        loading={actionLoading}
      />

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Reject Payment
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Please provide a reason for rejecting this payment.
                      </p>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="mt-3 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Enter rejection reason..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={executeRejection}
                  disabled={!rejectionReason.trim() || actionLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Rejecting...
                    </div>
                  ) : (
                    "Reject Payment"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectionModal(false);
                    setSelectedPayment(null);
                    setRejectionReason("");
                  }}
                  disabled={actionLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
